import type { TenantConfig } from '@webmono/storefront-ui/types'

export const sakuraSushi: TenantConfig = {
  id: 'sakura',
  slug: 'sakura-sushi',
  name: 'Sakura Sushi',
  tagline: 'Modern Japanese Cuisine',
  description: 'Experience contemporary Japanese cuisine with the freshest fish and innovative flavor combinations. Omakase-style dining and grab-and-go options available.',

  branding: {
    logo: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=200&h=200&fit=crop',
    logoAlt: 'Sakura Sushi Logo',
    favicon: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=32&h=32&fit=crop',
    bannerImage: 'https://images.unsplash.com/photo-1553621042-f6e147245754?w=1200&h=400&fit=crop',
    ogImage: 'https://images.unsplash.com/photo-1553621042-f6e147245754?w=1200&h=630&fit=crop',
  },

  theme: {
    colors: {
      primary: '#1A1A2E',
      primaryForeground: '#FFFFFF',
      secondary: '#F8F8FC',
      secondaryForeground: '#1A1A2E',
      accent: '#E84A5F',
      accentForeground: '#FFFFFF',
      background: '#FFFFFF',
      foreground: '#1A1A2E',
      muted: '#F4F4F8',
      mutedForeground: '#6B6B7B',
      card: '#FFFFFF',
      cardForeground: '#1A1A2E',
      border: '#E8E8F0',
      input: '#E8E8F0',
      ring: '#E84A5F',
      destructive: '#DC2626',
      destructiveForeground: '#FFFFFF',
      success: '#059669',
      successForeground: '#FFFFFF',
      warning: '#D97706',
      warningForeground: '#FFFFFF',
    },
    fonts: {
      heading: 'DM Sans',
      body: 'Inter',
      mono: 'JetBrains Mono',
    },
    radius: 'sm',
    spacing: 'relaxed',
  },

  contact: {
    address: '888 Pacific Avenue',
    city: 'San Francisco',
    state: 'CA',
    zip: '94115',
    country: 'USA',
    phone: '(415) 555-0888',
    email: 'hello@sakurasushi.com',
    coordinates: { lat: 37.7855, lng: -122.4324 },
  },

  operations: {
    timezone: 'America/Los_Angeles',
    hours: {
      monday: { open: '11:30', close: '21:30' },
      tuesday: { open: '11:30', close: '21:30' },
      wednesday: { open: '11:30', close: '21:30' },
      thursday: { open: '11:30', close: '22:00' },
      friday: { open: '11:30', close: '22:30' },
      saturday: { open: '12:00', close: '22:30' },
      sunday: { open: '12:00', close: '21:00' },
    },
    businessHours: { open: '11:30', close: '21:30' },
    taxRate: 0.0875,
    currency: 'USD',
    locale: 'en-US',
  },

  features: {
    pickup: true,
    delivery: true,
    tableOrdering: true,
    scheduledOrders: true,
    loyalty: true,
    promoCode: true,
    guestCheckout: true,
    accountRequired: false,
    tipping: true,
    specialInstructions: true,
    dietaryFilters: true,
  },

  fulfillment: {
    pickup: {
      enabled: true,
      leadTimeMinutes: 25,
      maxScheduleDays: 3,
    },
    delivery: {
      enabled: true,
      provider: 'doordash',
      flatFeeCents: 599,
      minimumOrderCents: 3000,
      radiusMiles: 4,
      leadTimeMinutes: 45,
      estimateMinutes: { min: 35, max: 50 },
    },
    tableService: {
      enabled: true,
      tableCount: 8,
      autoCloseMinutes: 90,
    },
  },

  loyalty: {
    enabled: true,
    programName: 'Sakura Club',
    pointsPerDollar: 15,
    tiers: [
      { id: 'member', name: 'Member', minPoints: 0, multiplier: 1, perks: ['Earn 15 points per $1'] },
      { id: 'silver', name: 'Silver', minPoints: 750, multiplier: 1.5, perks: ['Earn 22.5 points per $1', 'Free miso soup with orders over $40'] },
      { id: 'gold', name: 'Gold', minPoints: 2500, multiplier: 2, perks: ['Earn 30 points per $1', 'Free appetizer monthly', 'Priority seating'] },
    ],
    redemptionOptions: [
      { id: 'r1', name: 'Free Edamame', pointsCost: 300, discountCents: 500, description: 'Free edamame appetizer' },
      { id: 'r2', name: '$10 Off', pointsCost: 1000, discountCents: 1000, description: 'Save $10 on your order' },
      { id: 'r3', name: 'Free Roll', pointsCost: 2000, discountCents: 1800, description: 'Any signature roll free' },
    ],
  },

  tipping: {
    enabled: true,
    presets: [18, 20, 22, 25],
    allowCustom: true,
    defaultPresetIndex: 1,
  },

  i18n: {
    defaultLocale: 'en',
    supportedLocales: ['en', 'ja'],
    translations: {
      en: {
        'cart.title': 'Your Order',
        'cart.empty': 'Your cart is empty',
        'checkout.pickup': 'Pickup',
        'checkout.delivery': 'Delivery',
        'checkout.table': 'Table Service',
      },
      ja: {
        'cart.title': 'ご注文',
        'cart.empty': 'カートは空です',
        'checkout.pickup': 'テイクアウト',
        'checkout.delivery': 'デリバリー',
        'checkout.table': 'テーブルサービス',
      },
    },
  },

  legal: {
    privacyPolicyUrl: '/privacy',
    termsUrl: '/terms',
    allergenDisclaimer: 'Our kitchen handles raw fish, shellfish, wheat, soy, and eggs. Please inform us of any allergies. Raw or undercooked fish may increase the risk of foodborne illness.',
  },

  social: {
    instagram: 'https://instagram.com/sakurasushi',
    tiktok: 'https://tiktok.com/@sakurasushi',
    yelp: 'https://yelp.com/biz/sakura-sushi',
  },
}
